<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import { FormInputType } from '../../../interfaces/input'
import { cva } from 'class-variance-authority'
import FormInputSimple from './form/InputSimple.vue'

const props = defineProps({
    type: {
        type: String as PropType<FormInputType>,
        default: FormInputType.TEXT
    },
    label: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: ''
    },
    unit: {
        type: String,
        default: ''
    },
    isStandalone: {
        type: Boolean,
        default: false
    },
    isSelected: {
        type: Boolean,
        default: false
    },
    isNarrow: {
        type: Boolean,
        default: false
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    customClasses: {
        type: String,
        default: ''
    }
})

const value = defineModel<string | number>({
    required: true
})

const containerStyles = computed(() =>
    cva('group flex p-4 425:px-6 items-center gap-1 425:gap-4', {
        variants: {
            isDefault: {
                true: 'border-secondary-gray-disabled'
            },
            isStandalone: {
                true: 'border rounded-lg 425:rounded-2xl'
            },
            isSelected: {
                true: 'bg-primary-light border-light-blue-primary'
            },
            isDisabled: {
                true: 'border-gray-disabled'
            }
        }
    })({
        isDefault: !props.isSelected && !props.isDisabled,
        isStandalone: props.isStandalone,
        isSelected: props.isSelected && !props.isDisabled,
        isDisabled: props.isDisabled
    })
)

const labelStyles = computed(() =>
    cva(
        'text-[13px] 425:text-[15px] font-normal leading-4 425:leading-[18px] w-full',
        {
            variants: {
                isDefault: {
                    true: 'text-gray-l-800 group-hover:text-gray-l-600'
                },
                isSelected: {
                    true: 'text-primary-info group-hover:text-sky-600'
                },
                isDisabled: {
                    true: 'text-gray-disabled'
                }
            }
        }
    )({
        isDefault: !props.isSelected && !props.isDisabled,
        isSelected: props.isSelected && !props.isDisabled,
        isDisabled: props.isDisabled
    })
)

const inputClearIconStyles = computed(() =>
    cva('', {
        variants: {
            isDefault: {
                true: 'text-gray-comment cursor-pointer'
            },
            isSelected: {
                true: 'text-light-blue-primary-medium cursor-pointer'
            },
            isDisabled: {
                true: 'text-gray-disabled cursor-not-allowed'
            }
        }
    })({
        isDefault: !props.isSelected && !props.isDisabled,
        isSelected: props.isSelected && !props.isDisabled,
        isDisabled: props.isDisabled
    })
)

const mobileInputStyles = computed(() =>
    cva(
        '425:hidden w-1/3 text-[15px] font-normal focus:outline-none text-right',
        {
            variants: {
                isDefault: {
                    true: 'text-gray-l-600 placeholder:text-gray-l-600'
                },
                isSelected: {
                    true: 'text-primary-info bg-primary-light placeholder:text-primary-info'
                },
                isDisabled: {
                    true: 'text-gray-disabled bg-white placeholder:text-gray-disabled'
                }
            }
        }
    )({
        isDefault: !props.isDisabled && !props.isSelected,
        isSelected: props.isSelected && !props.isDisabled,
        isDisabled: props.isDisabled
    })
)
</script>

<template>
    <div :class="[containerStyles, !isStandalone && customClasses]">
        <div v-if="$slots.default" class="flex items-center">
            <slot />
        </div>

        <label v-if="label" :for="label" :class="labelStyles">
            {{ label }}
        </label>
        <FormInputSimple
            v-model="value"
            :type
            :placeholder
            :is-selected
            :is-disabled
            :unit
            class="hidden 425:block ml-auto"
            :class="isNarrow && 'w-[130px]'"
        >
            <template v-if="value !== ''" #rightIcon>
                <IconXCircle
                    :class="inputClearIconStyles"
                    is-solid
                    @click="value = ''"
                />
            </template>
        </FormInputSimple>
        <input
            v-model="value"
            :type
            :placeholder
            class="[&::-webkit-calendar-picker-indicator]:hidden"
            :class="mobileInputStyles"
        />
        <span
            v-if="unit"
            class="425:hidden text-[15px] font-normal text-zinc-800"
        >
            {{ unit }}
        </span>
    </div>
</template>
